/**
 * Ashrafie API Client
 *
 * Thin fetch wrapper for the Ashrafie loyalty-to-charity rewards ledger.
 * Uses platform-wide AmalQ JWT authentication. Supports the per-request
 * X-Idempotency-Key header required by POST /redemptions.
 *
 * @module lib/api/ashrafie/client
 */

import { API_CONFIG, buildApiUrl } from '../config';
import { clearAuthTokens, getAccessToken, isAuthenticated } from '../auth';
import { createTimeoutSignal } from '../timeout';

const ASHRAFIE_API_PATH = '/ashrafie';

// ─────────────────────────────────────────────────────────────────────────────
// Error class
// ─────────────────────────────────────────────────────────────────────────────

export class AshrafieApiError extends Error {
  readonly statusCode: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;

  constructor(
    message: string,
    statusCode: number,
    code: string = 'ASHRAFIE_API_ERROR',
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'AshrafieApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;
  }

  static async fromResponse(response: Response): Promise<AshrafieApiError> {
    let message = response.statusText || 'Request failed';
    let code = `HTTP_${response.status}`;
    let details: Record<string, unknown> | undefined;

    try {
      const data = await response.json();
      if (typeof data.message === 'string') message = data.message;
      if (typeof data.error === 'string') message = data.error;
      if (typeof data.code === 'string') code = data.code;
      if (data.details && typeof data.details === 'object') {
        details = data.details as Record<string, unknown>;
      }
    } catch {
      // Body was not JSON; fall back to status text.
    }

    return new AshrafieApiError(message, response.status, code, details);
  }

  /** True when the backend (or network) says "slow down." */
  get isRateLimited(): boolean {
    return this.statusCode === 429;
  }

  /** True when the user is unauthenticated / token expired. */
  get isUnauthorized(): boolean {
    return this.statusCode === 401;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Request options
// ─────────────────────────────────────────────────────────────────────────────

interface RequestOptions {
  requiresAuth?: boolean;
  timeout?: number;
  /** Extra headers merged on top of auth + content-type. */
  headers?: Record<string, string>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Client
// ─────────────────────────────────────────────────────────────────────────────

class AshrafieApiClient {
  private readonly timeout: number = API_CONFIG.timeout;

  isAuthenticated(): boolean {
    return isAuthenticated();
  }

  private buildUrl(endpoint: string): string {
    const clean = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return buildApiUrl(`${ASHRAFIE_API_PATH}/${clean}`);
  }

  private buildHeaders(options: RequestOptions = {}): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    };

    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (options.requiresAuth) {
      throw new AshrafieApiError(
        'Authentication required. Please sign in to view your Ashrafie wallet.',
        401,
        'AUTH_REQUIRED'
      );
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthTokens();
      }
      throw await AshrafieApiError.fromResponse(response);
    }

    const payload = await response.json();

    if (payload && typeof payload === 'object' && 'success' in payload) {
      if (payload.success === false) {
        throw new AshrafieApiError(
          payload.message || payload.error || 'Request failed',
          response.status,
          typeof payload.error === 'string' ? payload.error : 'API_ERROR'
        );
      }
      return payload.data as T;
    }

    return payload as T;
  }

  /**
   * Same as handleResponse, but preserves top-level pagination metadata when
   * the endpoint returns `{ data, pagination }` (cursor-paginated list routes).
   */
  private async handlePaginatedResponse<T>(
    response: Response
  ): Promise<{ items: T[]; pagination: { nextCursor: string | null; hasMore: boolean } }> {
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthTokens();
      }
      throw await AshrafieApiError.fromResponse(response);
    }

    const payload = await response.json();
    if (payload?.success === false) {
      throw new AshrafieApiError(
        payload.message || payload.error || 'Request failed',
        response.status,
        typeof payload.error === 'string' ? payload.error : 'API_ERROR'
      );
    }

    return {
      items: Array.isArray(payload?.data) ? (payload.data as T[]) : [],
      pagination: {
        nextCursor: payload?.pagination?.nextCursor ?? null,
        hasMore: Boolean(payload?.pagination?.hasMore),
      },
    };
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    const response = await fetch(this.buildUrl(endpoint), {
      method: 'GET',
      headers: this.buildHeaders(options),
      signal: createTimeoutSignal(options?.timeout ?? this.timeout),
    });
    return this.handleResponse<T>(response);
  }

  async getPaginated<T>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<{ items: T[]; pagination: { nextCursor: string | null; hasMore: boolean } }> {
    const response = await fetch(this.buildUrl(endpoint), {
      method: 'GET',
      headers: this.buildHeaders(options),
      signal: createTimeoutSignal(options?.timeout ?? this.timeout),
    });
    return this.handlePaginatedResponse<T>(response);
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    const response = await fetch(this.buildUrl(endpoint), {
      method: 'POST',
      headers: this.buildHeaders(options),
      body: body ? JSON.stringify(body) : undefined,
      signal: createTimeoutSignal(options?.timeout ?? this.timeout),
    });
    return this.handleResponse<T>(response);
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    const response = await fetch(this.buildUrl(endpoint), {
      method: 'PATCH',
      headers: this.buildHeaders(options),
      body: body ? JSON.stringify(body) : undefined,
      signal: createTimeoutSignal(options?.timeout ?? this.timeout),
    });
    return this.handleResponse<T>(response);
  }
}

export const ashrafieApi = new AshrafieApiClient();
